import { useEffect, useState } from "react";
import { apiFetch } from "../utils/wkCommon";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* LOGIC FETCH + FILTER */
export default function useSongsData({
  mode,
  searchValue,
  selectedPlaylist,
  appliedFilters,
  reloadKey,
  page
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [meta, setMeta] = useState({
    page: 1,
    pageSize: 25,
    total: 0
  });

  useEffect(() => {
    function isSearchValid(v) {
      if (!v) return false;

      const trimmed = v.trim();

      // Mandarin (minimal 1 char)
      if (/[\u4e00-\u9fff]/.test(trimmed)) return true;

      // Latin minimal 3 huruf
      if (/[a-zA-Z]/.test(trimmed) && trimmed.length >= 3) return true;

      return false;
    }

    async function load() {
      setLoading(true);

      try {
        // PLAYLIST
        if (mode === "album" && !selectedPlaylist) {
          const url = `${API_URL}/api/song-playlists?populate[0]=song_playlist_items`;

          const json = await apiFetch(url);
          const data = json?.data || [];

          const normalized = data.map(i => i.attributes || i);

          setRows(normalized);
          return;
        }

        // PLAYLIST DETAIL
        if (mode === "album" && selectedPlaylist) {
          const params = new URLSearchParams({
            "filters[song_playlist][documentId][$eq]": selectedPlaylist.documentId,
            "sort[0]": "orderNo:asc",
            "populate[virtuous_song][populate][0]": "song_files"
          });

          if (searchValue) {
            params.append(
              "populate[virtuous_song][filters][$or][0][JudulNada][$containsi]",
              searchValue
            );
            params.append(
              "populate[virtuous_song][filters][$or][1][JudulNadaPinyin][$containsi]",
              searchValue
            );
            params.append(
              "populate[virtuous_song][filters][$or][2][JudulLirik][$containsi]",
              searchValue
            );
            params.append(
              "populate[virtuous_song][filters][$or][3][JudulLirikPinyin][$containsi]",
              searchValue
            );
          }

          const url = `${API_URL}/api/song-playlist-items?${params.toString()}`;

          const json = await apiFetch(url);
          const data = json?.data || [];

          const normalized = data
            .map(i => {
              const item = i.attributes || i;

              if (!item.virtuous_song) return null;

              return {
                id: item.id,
                ...item.virtuous_song
              };
            })
            .filter(Boolean);

          setRows(normalized);
          return;
        }      

        // ================= TAB DAFTAR (EDITOR MODE) =================
        if (mode === "daftar") {
          const params = new URLSearchParams({
            "pagination[page]": page || 1,
            "pagination[pageSize]": 25,
            "populate[song_files][populate][0]": "mediaFile"
          });

          // SEARCH
          if (searchValue) {
            const q = searchValue.trim();
            params.append("filters[$or][0][JudulNada][$containsi]", q);
            params.append("filters[$or][1][JudulNadaPinyin][$containsi]", q);
            params.append("filters[$or][2][JudulLirik][$containsi]", q);
            params.append("filters[$or][3][JudulLirikPinyin][$containsi]", q);
            params.append("filters[$or][4][notes][$containsi]", q);
            params.append("filters[$or][5][song_text][$containsi]", q);
            params.append("filters[$or][6][song_translation][$containsi]", q);
          }

          // LANGUAGE
          appliedFilters.primary_language.forEach((v, i) => {
            params.append(`filters[PrimaryLanguage][$in][${i}]`, v);
          });

          if (appliedFilters.no_primary_language) {
            params.append("filters[PrimaryLanguage][$null]", "true");
          }

          // SORT
          const sortMap = {
            updatedAt: "updatedAt:desc",
            createdAt: "createdAt:desc",
            judulLirik: "JudulLirik:asc",
            judulNada: "JudulNada:asc"
          };

          params.append("sort[0]", sortMap[appliedFilters.sort || "updatedAt"]);

          const url = `${API_URL}/api/virtuous-songs?${params.toString()}`;
          const json = await apiFetch(url);

          const data = json?.data || [];
          const pg = json?.meta?.pagination || {};

          let normalized = data.map(i => i.attributes || i);

          // ===== FILE FILTER =====
          const hasType = (s, t) =>
            (s.song_files || []).some(f => f.filetype === t);

          if (appliedFilters.no_file) {
            normalized = normalized.filter(s => !(s.song_files || []).length);
          }
          if (appliedFilters.no_text_file) {
            normalized = normalized.filter(s => !hasType(s, "Text"));
          }
          if (appliedFilters.no_slide_file) {
            normalized = normalized.filter(s => !hasType(s, "Slides"));
          }
          if (appliedFilters.no_audio_file) {
            normalized = normalized.filter(s => !hasType(s, "Audio"));
          }
          if (appliedFilters.no_video_file) {
            normalized = normalized.filter(s => !hasType(s, "Video"));
          }

          // ===== TAG FILTER =====
          if (appliedFilters.no_emotion_tags) {
            normalized = normalized.filter(s => !(s.EmotionTags || []).length);
          }

          if (appliedFilters.no_keyword_tags) {
            normalized = normalized.filter(s => !(s.KeywordTags || []).length);
          }

          setMeta({
            page: pg.page || 1,
            pageSize: pg.pageSize || 25,
            total: pg.total || 0
          });

          setRows(normalized);
          return;
        }

        let all = [];
        let pageLoop = 1;

        // ================= TAB SONG ================= 
        const hasSearch = isSearchValid(searchValue);
        const hasFilter =
          appliedFilters.primary_language.length ||
          appliedFilters.emotion_tags.length ||
          appliedFilters.keyword_tags.length;

        // ❌ kondisi kosong semua → jangan fetch
        if (mode === "song" && !hasSearch && !hasFilter) {
          setRows([]);
          setLoading(false);
          return;
        }

        while (true) {
          const params = new URLSearchParams({
            "pagination[page]": pageLoop,
            "pagination[pageSize]": 50
          });

          if (hasSearch) {
            const q = searchValue.trim();

            params.append("filters[$or][0][JudulNada][$containsi]", q);
            params.append("filters[$or][1][JudulNadaPinyin][$containsi]", q);
            params.append("filters[$or][2][JudulLirik][$containsi]", q);
            params.append("filters[$or][3][JudulLirikPinyin][$containsi]", q);
            params.append("filters[$or][4][notes][$containsi]", q);
            params.append("filters[$or][5][song_text][$containsi]", q);
            params.append("filters[$or][6][song_translation][$containsi]", q);
          }
          appliedFilters.primary_language.forEach((v, i) => {
            params.append(`filters[PrimaryLanguage][$in][${i}]`, v);
          });

          const url = `${API_URL}/api/virtuous-songs?${params.toString()}&populate[song_files][populate][0]=mediaFile`;

          const json = await apiFetch(url);
          const data = json?.data || [];

          if (!data.length) break;

          const normalized = data.map(i => i.attributes || i);
          all = [...all, ...normalized];

          //"normalized:",all)
          let filtered = all;

          if (appliedFilters.emotion_tags.length) {
            filtered = filtered.filter(song =>
              appliedFilters.emotion_tags.some(f =>
                (song.EmotionTags || []).some(t =>
                  t.id === f.id || t.zh === f.zh
                )
              )
            );
          }

          if (appliedFilters.keyword_tags.length) {
            filtered = filtered.filter(song =>
              appliedFilters.keyword_tags.some(f =>
                (song.KeywordTags || []).some(t =>
                  t.id === f.id || t.zh === f.zh
                )
              )
            );
          }

          if (filtered.length >= 50) {
            setRows(filtered.slice(0, 50));
            return;
          }

          pageLoop++;
        }

        let final = all;

        if (appliedFilters.emotion_tags.length) {
          final = final.filter(song =>
            appliedFilters.emotion_tags.some(f =>
              (song.EmotionTags || []).some(t =>
                t.id === f.id || t.zh === f.zh
              )
            )
          );
        }

        if (appliedFilters.keyword_tags.length) {
          final = final.filter(song =>
            appliedFilters.keyword_tags.some(f =>
              (song.KeywordTags || []).some(t =>
                t.id === f.id || t.zh === f.zh
              )
            )
          );
        }

        setRows(final.slice(0, 50));

      } finally {
        setLoading(false);
      }
    }

    load();
  }, [mode, searchValue, selectedPlaylist, appliedFilters, reloadKey]);

  return { rows, loading, meta };
}